const PYODIDE_BUILD = '/pyodide/build/';

export const BLOCKLISTED_IMPORTS = new Set(['js', 'webbrowser', 'micropip', 'pyodide', 'importlib']);

/** Start setup for Pyodide autograder */

// Primary setup script. Sets up variables and functions available to grading scripts

const AUTOGRADE_SETUP = `
# **** Variables to track state between input and output callbacks ****

# Running history of stdouts
__autograde_outputs = []

# Stores log of outputs and inputs
# {"output": <str>, "input" ?: <str>, "expected_output": <str>, "expected_input" ?: <str>, "variable_descriptions" ?: {<var_name>: <description>}}
# i.e. input, if exists, is paired with its prompt, and we track the expected values for each
__autograde_trace = []

# Index of the next trace item to process (need to know where to start after processing a section before grade_input())
__autograde_trace_index = 0

# Set up classes to wrap successful/failed state transitions
class TransitionResult:
    """Base class for all transition results"""
    pass

class FailedTransition(TransitionResult):
    def __init__(self, observed: str, expected: str, error_msg: str, variable_descriptions: dict = {}):
        self.observed = observed
        self.expected = expected
        self.error_msg = error_msg
        self.variable_descriptions = variable_descriptions

class FailedTransitionWithInput(FailedTransition):
    def __init__(self, observed_output: str, example_input: str, error_msg: str):
        super().__init__(observed_output, "", error_msg)
        self.example_input = example_input

class SuccessfulTransition(TransitionResult):
    def __init__(self):
        pass

class InputTransitionResult:
    """Base class for input transition results"""
    pass

class SuccessfulInputTransition(InputTransitionResult):
    def __init__(self, dynamic_input: str):
        self.dynamic_input = dynamic_input

class FailedInputTransition(InputTransitionResult):
    def __init__(self, error_msg: str):
        self.error_msg = error_msg

class FailedInputTransitionWithOutput(FailedInputTransition):
    def __init__(self, error_msg: str, example_input: str):
        super().__init__(error_msg)
        self.example_input = example_input

class FinishStateResult:
    """Base class for finish state results"""
    pass

class SuccessfulFinishState(FinishStateResult):
    def __init__(self):
        pass

class FailedFinishState(FinishStateResult):
    def __init__(self, error_msg: str):
        self.error_msg = error_msg


# For autograde scripts to store data that must be persisted between
# calls to grade_input and grade_output
__autograde_aux_data = {} 

# Relevant if using the autograder for randomness:
# Variables that have been registered to have their distributions checked over many iterations.
# At the end of the script, these variables are transfered to a JS object that accumulates
# their observed values for every iteration.
# Format:
# {
#   "var_name": {
#     "dist": "uniform", (more support coming!)
#     "params": {...}, (depends on distribution, {"min": int, "max": int} for "uniform")
#     "outputs": [] (the values seen for this var in this program),
#     "error_str": ""
#   }
# }
__autograde_registered_vars = {}

# Stores the error state for the autograder
__autograde_error_state = {
  "has_error": False
}

# Define a custom AutogradeError exception, so we can raise these but catch all others in
# the autograder scripts
class AutogradeError(Exception):
  pass

# **** @joshdelg Functions exposed to autograding scripts ****

# Inovked by autograding script to update the error state when its clear a student has failed the test.
# Updates the error state ONLY ONCE. This prevents a meaningful error from being replaced by a Pyodide/Unthrow
# exception that was caused by the meaningful one.

def autograde_error(message):
  if not __autograde_error_state["has_error"]:
    __autograde_error_state["has_error"] = True
    __autograde_error_state["message"] = message

    raise AutogradeError(f"Autograder detected failed test: {message}")


# Invoked by autograding script to:
# 1) Define a random variable that we will check the distribution of
# 2) Add a particular value to the observed values for a variable.
# 3) Handle Early Stopping: if the observed value is illegal for the variable type
# (i.e. min is 10 and we see 5), an exception is raised and testing stops.
# Right now, only the "uniform" distribution is implemented.

def register_random(rv, var_name, dist=None, params=None, error_str=None):
  # If registering for the first time, add to dict. Otherwise, just add value to outputs
  if var_name not in __autograde_registered_vars:
    if dist is None and params is None:
      raise Exception("If adding new variable, must provide distribution and parameters")
    
    __autograde_registered_vars[var_name] = {
      "dist": dist, "params": params, "outputs": [rv], "error_str": error_str
    }
  
  else:
    __autograde_registered_vars[var_name]["outputs"].append(rv)

  # Early stopping on illegal values
  cur_var = __autograde_registered_vars[var_name]

  if cur_var["dist"] == "uniform":
    if rv < cur_var["params"]["min"]:
      autograde_error(f"Expected variable to have minimum {cur_var['params']['min']} but found {rv}")
    
    if rv > cur_var["params"]["max"]:
      autograde_error(f"Expected variable to have maximum {cur_var['params']['max']} but found {rv}")


# **** Additional unthrow handler to run autograde script on stdout ****

# Override print function to call handler (only in test mode. \`*stdout\` because print takes varargs.)
if unthrow.test_mode:
  print=lambda *stdout : cip___throwOutput(stdout=stdout)

# Output handler that:
# 1) Join potential varargs into one string (empty goes to "")
# 2) Splits string into lines and appends to the trace
# 3) Passes original varargs to print()
# Note: Optionally yield to JS to view data/outputs for debugging
# Note: grade_output() autograder called lazily in grade_input/finish()

def cip___throwOutput(stdout):
    global __unthrowActiveCommand__

    # Join potential varargs into one string (empty goes to "")
    out = " ".join([str(s) for s in stdout])
    __autograde_outputs.append(out)

    # Split into lines before appending to trace
    for line in out.split("\\n"):
      __autograde_trace.append({"output": line})
    
    __builtin__.print(*stdout)
    
    # Set to True and print out \`data\` in unthrow handler to debug. Not necessary for autograder. And slow.
    if False:
      __unthrowActiveCommand__["cmd"] = "output"
      __unthrowActiveCommand__["data"] = {
        "output_vars": __autograde_aux_data,
        "outputs": __autograde_outputs
      }
      unthrow.stop("output")

    __unthrowActiveCommand__ = dict()
`;

// Small modification to Unthrow throwInput handler to run the grade_input script.
const AUTOGRADE_INPUT_INJECTION = `
    # **** Modification to throwInput handler for Advanced Autograder ****

    # 0. (print() to print the prompt already called)
    # 1. Repeatedly run grade_output() on each line of the trace (since last call to input())
    #   - Trace entries already split line by line
    # 2. Run grade_input() -- if the state should not expect input, should throw error
    # 3. Update the trace with input and move trace index to past this input

    global __autograde_trace_index
    global __autograde_trace

    for i, t in enumerate(__autograde_trace[__autograde_trace_index:]):
      cur_line = t["output"]

      try:
        result = grade_output(cur_line, __autograde_aux_data)
      except Exception as e:
        if isinstance(e, unthrow.ResumableException):
          raise e
        else:
          autograde_error(f"{e}")

      # Succeeded: Use what we recieved as expected output (since we know was correct)
      if isinstance(result, SuccessfulTransition):
        __autograde_trace[__autograde_trace_index + i]["expected_output"] = cur_line

      # Two failures cases:
      if isinstance(result, FailedTransition):
        # Case 1: We expect input in this state:
        # - *previous* output should be the prompt, so we add expected input there
        # - Since we're in grade_input, we must've called input at some point, so we
        #   keep all the output lines in our trace, and add observed input (an example provided
        #   by the script) as expected input
        if isinstance(result, FailedTransitionWithInput):
          __autograde_trace[__autograde_trace_index + i - 1]["expected_input"] = result.example_input
          __autograde_trace[-1]["input"] = result.example_input

          # NOTE: We do not truncate __autograde_trace here, because we want to keep
          # all the output lines in our trace, and add observed input to the last line

        # Case 2: Normal Failure:
        # - Truncate autograder trace at this point
        # - Add expected output with possible variable substitutions
        else:
          __autograde_trace[__autograde_trace_index + i]["expected_output"] = result.expected
          __autograde_trace[__autograde_trace_index + i]["variable_descriptions"] = result.variable_descriptions
          __autograde_trace = __autograde_trace[:__autograde_trace_index + i + 1]
        
        # In either case, throw error
        autograde_error(f"{result.error_msg}")

    # We're done matching states on output, so we can run grade_input()
    try:
      result = grade_input(__autograde_aux_data)
    except Exception as e:
      if isinstance(e, unthrow.ResumableException):
        raise e
      else:
        autograde_error(f"{e}")

    # If transition failed -- don't proceed, throw relevant error
    if isinstance(result, FailedInputTransition):
      
      # We entered grade_input() in a state where we expected output
      # We add an input to the most recent output trace and make expected input = ""
      if isinstance(result, FailedInputTransitionWithOutput):
        __autograde_trace[-1]["input"] = result.example_input
        __autograde_trace[-1]["expected_input"] = ""

      __autograde_trace = __autograde_trace[:__autograde_trace_index + i + 1]
      autograde_error(result.error_msg)

    # Success: Append the input to the trace (as the input field of the most recent output)
    __autograde_trace[-1]["input"] = result.dynamic_input
    __autograde_trace[-1]["expected_input"] = result.dynamic_input

    # Update trace index so we slice on outputs after these
    __autograde_trace_index = len(__autograde_trace)

    if result.dynamic_input:
      if not isinstance(result.dynamic_input, str):
        raise Exception(f"grade_input must return a str or None! Instead, got {result.dynamic_input}, {type(result.dynamic_input)}")

      return result.dynamic_input
`;

const AUTOGRADE_FINISH_INJECTION = `
    # **** Additional logic on Unthrow finish for Advanced Autograder ****
    # 1. Repeatedly run grade_output() on each line of the trace (since last call to input())
    # 2. Run grade_finish() -- if not correct finished state, should throw error

    global __autograde_trace_index
    # global __autograde_trace -- declared global earlier in script

    # Already split by lines before added to trace
    for i, t in enumerate(__autograde_trace[__autograde_trace_index:]):
      cur_line = t["output"]

      try:
        result = grade_output(cur_line, __autograde_aux_data)
      except Exception as e:
        if isinstance(e, unthrow.ResumableException):
          raise e
        else:
          autograde_error(f"{e}")

      # Succeeded: Use what we recieved as expected output (since we know was correct)
      if isinstance(result, SuccessfulTransition):
        __autograde_trace[__autograde_trace_index + i]["expected_output"] = cur_line

      # Failed: Truncate autograder trace at this point, add possible variable substitutions, and err
      if isinstance(result, FailedTransition):
        __autograde_trace[__autograde_trace_index + i]["expected_output"] = result.expected
        __autograde_trace[__autograde_trace_index + i]["variable_descriptions"] = result.variable_descriptions
        __autograde_trace = __autograde_trace[:__autograde_trace_index + i + 1]
        autograde_error(result.error_msg)


    # If all previous outputs matched, run grade_finish()
    try:
      result = grade_finish(__autograde_aux_data)
    except Exception as e:
      if isinstance(e, unthrow.ResumableException):
        raise e
      else:
        autograde_error(f"{e}")

    # If we didn't reach a correct finished state, throw error
    if isinstance(result, FailedFinishState):
      autograde_error(result.error_msg)

    # Otherwise, we're done!
`;

/** End setup for Pyodide autograder. */

/**
 * Builds the sandbox for the Python code that runs in the Pyodide environment.
 * @param stepMode - Whether to run in step mode.
 * @param testMode - Whether to run in test mode.
 * @param uninterrupted - The number of uninterrupted steps before
 * escaping via unthrow.
 * @param inputSize - The size of the input.
 * @param isAdvancedAutograder - Whether to setup the advanced
 * autograder's functionality.
 * @param gradingScripts - The advanced autograder scripts.
 * @param projectId - The project ID.
 * @param courseId - The course ID.
 * @returns {string} - The setup script.
 */
const SETUP_SCRIPT = (
  stepMode,
  testMode,
  uninterrupted,
  inputSize = 0,
  isAdvancedAutograder,
  gradingScripts = null,
  projectId,
  courseId,
) => {
  return `
from __future__ import print_function
try:
    import __builtin__
except ImportError:
    # Python 3
    import builtins as __builtin__
import time
import sys
finished = False
import unthrow
import pyodide
import js
____step_freq = ${uninterrupted}
unthrow.step_mode = ${stepMode ? 'True' : 'False'}
unthrow.test_mode = ${testMode ? 'True' : 'False'}
unthrow.term_log = []
unthrow.max_lines = 100000
unthrow.step_list = []
unthrow.max_sl_size = 1000
unthrow.rec_depth = 800
js.projectId = "${projectId}"
js.courseId = "${courseId}"
time.sleep=lambda seconds: cip___throwSleep(seconds)
input=lambda printMessage="" : cip___throwInput(printMessage=printMessage)
exec=lambda code="" : cip___exec(code=code)
eval=lambda code="" : cip___eval(code=code)
__import__=lambda name, globals=None, locals=None, fromlist=(), level=0: cip____import__(name=name, globals=globals, locals=locals, fromlist=fromlist, level=level)

__input_ctr_test__ = 0
__input_sze_test__ = ${inputSize}
__resumer=unthrow.Resumer()
__resumer.set_interrupt_frequency(____step_freq);
__unthrowActiveCommand__ = dict()
____unthrowActiveInput = ''
def cip___awaitclick():
    global __unthrowActiveCommand__
    if unthrow.test_mode:
        return 0
    __unthrowActiveCommand__["cmd"] = "awaitclick"
    unthrow.stop("awaitclick")
    __unthrowActiveCommand__ = dict()
def cip___noai_chat(request):
    """Allows the user to call the NotOpenAI API from the main script. 
    """ 
    global __unthrowActiveCommand__
    __unthrowActiveCommand__["cmd"] = "noai_chat"
    # N.b. Pyodide converts the Python dict into a Node Map.
    __unthrowActiveCommand__["data"] = request
    unthrow.stop("noai_chat")
    # Once we resume, the response should be set in the global \`jsAiClient.response\`
    # or an error should be set in \`jsAiClient.error\`
    __unthrowActiveCommand__ = dict()
    if js.jsAiClient.error:
        raise js.jsAiClient.error
    return js.jsAiClient.response
def cip___minidb_func(reqtype, data):
    """Allows the user to call the jsAiClient API from the main script. 
    """ 
    global __unthrowActiveCommand__
    __unthrowActiveCommand__["cmd"] = "minidb"
    __unthrowActiveCommand__["data"] = {
        "reqtype": reqtype,
        "data": data
    }
    unthrow.stop("minidb")
    __unthrowActiveCommand__ = dict()
    return
def cip___throwSleep(seconds):
    global __unthrowActiveCommand__
    if unthrow.test_mode:
        return 0
    __unthrowActiveCommand__["cmd"] = "sleep"
    __unthrowActiveCommand__["data"] = seconds
    unthrow.stop("sleep")
    __unthrowActiveCommand__ = dict()
def cip___throwInput(printMessage):
    # Pyodide autograder adds a cip__throwOutput exception to run
    # the grade_output autograding script. We must call \`print\` before throwing
    # input exception so we can first throw output exception.
    global __unthrowActiveCommand__
    print(printMessage)

    # Interpolate the modification to input exception needed for autograder (if we
    # provided autograding scripts). Returns, will not run rest of this function!
    ${isAdvancedAutograder ? AUTOGRADE_INPUT_INJECTION : ''}

    __unthrowActiveCommand__["cmd"] = "input"
    __unthrowActiveCommand__["data"] = printMessage
    
    if unthrow.test_mode:
        global __input_sze_test__, __input_ctr_test__
        if  __input_ctr_test__ >= __input_sze_test__:
            return ""
        __input_ctr_test__+=1
    unthrow.stop("input")
    __unthrowActiveCommand__ = dict()
    return ____unthrowActiveInput
def cip___exec(code):
    raise Exception("The exec() function is not supported")
def cip___eval(code):
    raise Exception("The eval() function is not supported")
def cip____import__(name, globals=None, locals=None, fromlist=(), level=0):
    raise Exception("The __import__() function is not supported")

# Bind the functions to the unthrow object so they can be called by library functions
unthrow.cip___awaitclick = cip___awaitclick
unthrow.cip___noai_chat = cip___noai_chat
unthrow.cip___minidb_func = cip___minidb_func

# **** @joshdelg BEGIN PYODIDE AUTOGRADER FUNCTIONALITY ****

# grade_input runs every time the student's code calls "input". Inside the function, you
# have access to several variables and functions.

# grade_output runs every time the student's code calls "print". Inside the function, you
# have access to several variables and functions.

# Both functions have access to the following functions and variables:
#   - "outputs": this is a list storing the history of stdout from the program so far. Typically, 
#   you'll want to retrieve the latest output (the one that called the grade_output function) through
#   outputs[-1]
#   - "output_vars": this is a dictionary storing any data stored from previous grade_output calls that
#   we want to use in a later grade_output call or grade_input call. Ex. In Khansole Academy, we store 
#   the two random numbers generated, so that we can access them in grade input and calculate the sum
#   to compare it to what the student outputted for their sum.
#   - "autograde_error(message, test_failed)": this is a function that should be called under any condition
#   that qualifies as "failing" (not matching an expected output, outputting an incorrect sum). "message" is
#   what will be outputted on the failed test dialogue, and name signifies what type of random test failed.
#   The options right now are "Random Formatting" (failing output regex), "Random Validation" (not matching a sum),
#   and "Random Range" (seeing 5 for a variable when the min should be 10). The function raises an error that causes
#   the program to yield back to NodeJS and passes the error state back to the webapp to be displayed.
#   - "register_random_variable(rv, var_name, dist=None, params=None, tolerance=0.0001)": this is a function used
#   to indicate that the "rv" is one observation of a random variable "var_name" who's distribution we'd like to
#   later check. Calling it with a particular "var_name" for the first time "instantiates" the variable by adding 
#   all parameters to __autograde_registered_vars. Subsequent calls to the same variable name only add the observation,
#   "rv", to the list of observations for that variable.

${isAdvancedAutograder ? gradingScripts.onInput : ''}
${isAdvancedAutograder ? gradingScripts.onOutput : ''}
${isAdvancedAutograder ? gradingScripts.onFinish : ''}

${isAdvancedAutograder ? AUTOGRADE_SETUP : ''}

# **** @End Pyodide autograder functionality ****
`;
};

const AUTOGRADE_INFINITE_LOOP_DETECTION = `
    # **** Modification to Unthrow finish logic: Infinite Loop detection for Advanced Autograder ****
    global __autograde_trace
    try:
        __resumer.run_once(mainApp, "x")
    except Exception as e:
        # If we reached maximum testing line count, throw a special timeout error
        if "TIMEOUT" in str(e):
            # Truncate the trace so its not 100k lines
            __autograde_trace = __autograde_trace[:100]

            # Run the grade_output/input loops so we can get some expected diff
            for i, t in enumerate(__autograde_trace):
                cur_line = t["output"]
                try:
                    result = grade_output(cur_line, __autograde_aux_data)
                except Exception as e:
                    if isinstance(e, unthrow.ResumableException):
                        raise e
                    else:
                        autograde_error(f"{e}")
                if isinstance(result, SuccessfulTransition):
                    __autograde_trace[i]["expected_output"] = cur_line
                else:
                    __autograde_trace[i]["expected_output"] = result.expected
                    
                    # We don't need to get the specific error message since we have the loop, just display a diff
                    break

            # Raise error
            autograde_error("Your program took too long to run. It's likely stuck in an infinite loop.")
        else:
            raise e
`;

const RUN_MAINAPP = isAdvancedAutograder => `
if not __resumer.finished:
    ${isAdvancedAutograder ? AUTOGRADE_INFINITE_LOOP_DETECTION : '__resumer.run_once(mainApp, "x")'}
else:
    finished = True
    ${isAdvancedAutograder ? AUTOGRADE_FINISH_INJECTION : ''}
`;

const RESET_MODULES = `
__resumer.cancel()
modules = []
for module in sys.modules:
  if "/home/pyodide" in str(sys.modules[module]):
    modules.append(module)
for module in modules:
  del sys.modules[module]
      `;

const RESET_REPL = `
sys.settrace(None)
modules = []
for module in sys.modules:
  if "/home/pyodide" in str(sys.modules[module]):
    modules.append(module)
for module in modules:
  del sys.modules[module]
      `;

const RESET_SCOPE = `
# if there is a current scope present
# transfer all of its variables in the current
# globals scope somewhere else
curr_scope = py_scope['current_scope']
# Always (almost) true
if curr_scope and curr_scope != new_scope:
    copy = py_scope['copy']
    py_scope['scopes'][curr_scope] = copy.copy(globals())
    # reset to init values first
    for var in list(globals().keys()):
        should_be_variables = list(py_scope['init_vars'].keys())
        should_be_variables += ['curr_scope', 'new_scope', 'copy', 'var']
        if var not in should_be_variables:
            del globals()[var]
    # add variables of the new scope if they exist
    globals().update(py_scope['scopes'].setdefault(new_scope, {}))
    del copy
    py_scope['current_scope'] = new_scope
del new_scope
del curr_scope
`;

/*
await micropip.install(
  '/pyodide/karel-0.0.1-py3-none-any.whl'
)
*/
const INIT_SYSTEM_CODE = `
import sys
import micropip
# from js import graphics
await micropip.install(
  '/pyodide/graphics-0.0.4-py3-none-any.whl'
)
await micropip.install(
  '/pyodide/karel-0.0.1-py3-none-any.whl'
)
await micropip.install(
  '/pyodide/ai-0.0.1-py3-none-any.whl'
)
await micropip.install(
  '/pyodide/database-0.0.1-py3-none-any.whl'
)
del micropip
`;

const SET_STEPPING_LISTS = `
step_list = unthrow.step_list`;

const REPL_SETUP = `
import sys
from pyodide import to_js
from pyodide.console import PyodideConsole, repr_shorten
import __main__
import js
import os
pyconsole = PyodideConsole(__main__.__dict__)
import builtins
async def await_fut(fut):
  res = await fut
  if res is not None:
    builtins._ = res
  return to_js([res], depth=1)
def clear_console():
  pyconsole.buffer = []
__line_cnt__ = 0
__repl_input__ = ""
__std__input = builtins.input
def __cip_repl_input(prompt=''):
    data = js.replInput(prompt)
    return data

builtins.input = __cip_repl_input
`;

const REPL_TRACE = `
__tf_ctr__ = 0
def trace_function(frame, event, arg):
    global __tf_ctr__
    __tf_ctr__  += 1

    if __tf_ctr__ > 110000:
        raise Exception("Stopping... The CIP Repl will only let you run about 100,000 total lines. Check to make sure all of your loops stop.")

    return trace_function

sys.settrace(trace_function)

`;

export const CHECK_IMPORTS = mainCodeString => {
  const escapeString = str => {
    return str
      .replace(/\\/g, '\\\\') // Escape backslashes
      .replace(/\n/g, '\\n') // Escape newlines
      .replace(/"/g, '\\"') // Escape double quotes
      .replace(/'/g, "\\'") // Escape single quotes
      .replace(/\t/g, '\\t') // Escape tabs
      .replace(/\r/g, '\\r'); // Escape carriage returns
  };

  const squashedCode = escapeString(mainCodeString);

  const codeString = `
import pyodide
user_code = "${squashedCode}"
____imports_list____ = pyodide.find_imports(user_code)
del user_code
del pyodide
____imports_list____
`;
  return codeString;
};

export {
  SETUP_SCRIPT,
  RUN_MAINAPP,
  RESET_MODULES,
  RESET_SCOPE,
  INIT_SYSTEM_CODE,
  PYODIDE_BUILD,
  SET_STEPPING_LISTS,
  REPL_SETUP,
  REPL_TRACE,
  RESET_REPL,
};
